// Every filter the boards offer, declared once.
//
// The pipeline board and the Listings tab used to each carry their own controls, their own
// state and their own query-string assembly, which is why the pipeline had 14 filters and the
// Listings tab had 5 with no overlap in the code. This file is the single list; the panel
// renders it, useListingFilters() holds the values, and toParams() turns them into the exact
// query string backend/api/filter_params.py declares. Adding a filter means adding a row here
// and a line in that file -- nothing else on the front end.
//
// SCOPES
// Both boards show every filter (`scopes` exists for the next board that needs a subset). On
// the Listings tab a sale-only filter such as cap rate simply matches no rentals; that is the
// agreed behaviour rather than controls that appear and disappear as you change Type.

import { RENOVATION_LABELS } from "../../utils/renovation";

export const GROUPS = [
  { key: "price", label: "Price" },
  { key: "unit", label: "Unit" },
  { key: "building", label: "Building" },
  { key: "carry", label: "Carrying costs" },
  { key: "listing", label: "Listing" },
];

// Condition == the AI renovation level. The scale is defined in
// backend/services/listing_analysis.py; level 5 doubles as "New" per product. The words come
// from utils/renovation.js so this dropdown and the client share page cannot drift apart —
// here they keep the number, which analysts filter by.
export const CONDITION_OPTIONS = Object.entries(RENOVATION_LABELS).map(([value, word]) => ({
  value: Number(value),
  label: `${value} · ${word}`,
}));

// Keep these in step with OUTDOOR_TYPES / _SERVICE_LEVEL_SQL / LISTING_TYPES in
// backend/services/listing_filters.py — a value not known there is rejected at save time.
export const OUTDOOR_TYPE_OPTIONS = [
  { value: "terrace", label: "Terrace" },
  { value: "balcony", label: "Balcony" },
  { value: "juliet", label: "Juliet" },
  { value: "garden", label: "Garden" },
  { value: "roof_deck", label: "Private roof deck" },
];

export const SERVICE_LEVEL_OPTIONS = [
  { value: "full_time_doorman", label: "Full-time doorman" },
  { value: "part_time_doorman", label: "Part-time doorman" },
  { value: "virtual_doorman", label: "Virtual doorman" },
  { value: "concierge", label: "Concierge" },
  { value: "live_in_super", label: "Live-in super" },
  { value: "none", label: "None" },
];

export const LISTING_TYPE_OPTIONS = [
  { value: "rls_broker", label: "RLS broker listing" },
  { value: "rls_private", label: "RLS private listing" },
  { value: "owner", label: "Owner listed" },
];

const YES_NO = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

// control:
//   range      -> two numeric inputs, keys `${key}_min` / `${key}_max`
//   currency   -> same, rendered with CurrencyInput
//   select     -> one dropdown, key `key`
//   chips      -> multi-select, key `key`, value is an array
//   days       -> one "within N days" dropdown
//   daterange  -> two date inputs, keys date_from / date_to
export const FILTERS = [
  // ── Price ────────────────────────────────────────────────────────────────────────────
  { key: "price", group: "price", label: "Asking price ($)", control: "currency" },
  { key: "ppsf", group: "price", label: "Price per SF ($)", control: "currency" },
  {
    key: "price_change_dir", group: "price", label: "Price change direction", control: "select",
    options: [
      { value: "increase", label: "Increase" },
      { value: "decrease", label: "Decrease" },
      { value: "none", label: "No change" },
    ],
    // The LATEST move, which is a different question from "since listed" below — the two can
    // carry opposite signs on the same listing.
    hint: "Direction of the most recent price move",
  },
  {
    key: "since_listed", group: "price", label: "Change since listed (%)", control: "range",
    step: "0.1", hint: "Cumulative vs the original ask. Cuts are negative.",
  },
  {
    key: "since_sale", group: "price", label: "Change since last sale (%)", control: "range",
    step: "0.1",
    // ~25% of active sales have a prior closed sale on record; the rest can never match.
    hint: "Only units with a recorded prior sale can match",
  },
  {
    key: "price_change_days", group: "price", label: "Last price change", control: "days",
    choices: [7, 14, 30, 60, 90, 180, 365],
  },

  // ── Unit ─────────────────────────────────────────────────────────────────────────────
  { key: "sqft", group: "unit", label: "Square footage", control: "range", step: "1" },
  { key: "beds", group: "unit", label: "Bedrooms", control: "range", step: "1" },
  { key: "baths", group: "unit", label: "Bathrooms", control: "range", step: "0.5" },
  {
    key: "floor", group: "unit", label: "Floor", control: "range", step: "1",
    hint: "Floor is known for ~61% of active sales",
  },
  { key: "outdoor", group: "unit", label: "Outdoor space", control: "select", options: YES_NO },
  {
    key: "outdoor_types", group: "unit", label: "Outdoor type", control: "chips",
    options: OUTDOOR_TYPE_OPTIONS,
    // Only meaningful alongside a yes; the panel reveals it when Outdoor space = Yes.
    revealedBy: { key: "outdoor", equals: "yes" },
  },
  { key: "washer_dryer", group: "unit", label: "Washer/dryer in unit", control: "select", options: YES_NO },
  { key: "central_air", group: "unit", label: "Central air", control: "select", options: YES_NO },
  {
    key: "reno", group: "unit", label: "Condition", control: "range",
    choices: CONDITION_OPTIONS, hint: "AI renovation level",
  },

  // ── Building ─────────────────────────────────────────────────────────────────────────
  {
    key: "service_level", group: "building", label: "Service level", control: "chips",
    options: SERVICE_LEVEL_OPTIONS,
  },
  {
    key: "elevator", group: "building", label: "Elevator", control: "select",
    options: [
      { value: "elevator", label: "Elevator" },
      { value: "walkup", label: "Walk-up" },
    ],
  },

  // ── Carrying costs ───────────────────────────────────────────────────────────────────
  { key: "cc", group: "carry", label: "Common charges ($/mo)", control: "currency" },
  { key: "cc_psf", group: "carry", label: "Common charges per SF ($/mo)", control: "range", step: "0.01" },
  {
    key: "tax", group: "carry", label: "Taxes ($/mo)", control: "currency",
    hint: "About half of active sales carry no tax figure",
  },
  { key: "carrying", group: "carry", label: "Total monthly carry ($)", control: "currency" },

  // ── Listing ──────────────────────────────────────────────────────────────────────────
  {
    key: "special", group: "listing", label: "Sale type", control: "select",
    options: [
      { value: "resell", label: "Resale" },
      { value: "sponsor", label: "Sponsor unit" },
      { value: "new_dev", label: "New development" },
    ],
  },
  {
    key: "listing_type", group: "listing", label: "Listing type", control: "chips",
    options: LISTING_TYPE_OPTIONS,
  },
  { key: "dom", group: "listing", label: "Days listed", control: "range", step: "1" },
  { key: "cap_rate", group: "listing", label: "AI cap rate (%)", control: "range", step: "0.1" },
  { key: "date", group: "listing", label: "Date listed", control: "daterange" },
];

/** The value keys one filter row owns — a range owns two, everything else owns one. */
export function keysFor(f) {
  if (f.control === "range" || f.control === "currency") return [`${f.key}_min`, `${f.key}_max`];
  if (f.control === "daterange") return ["date_from", "date_to"];
  return [f.key];
}

/** Every value key the panel manages, in spec order. */
export const ALL_KEYS = FILTERS.flatMap(keysFor);

/** A blank value set: "" for scalars, [] for the multi-selects. */
export function emptyValues() {
  const out = {};
  for (const f of FILTERS) {
    for (const k of keysFor(f)) out[k] = f.control === "chips" ? [] : "";
  }
  return out;
}

/** True when this key is actually constraining anything. */
export function isSet(v) {
  if (Array.isArray(v)) return v.length > 0;
  return v !== "" && v !== null && v !== undefined && v !== "all";
}

/**
 * Values -> query params, in the shape backend/api/filter_params.py declares.
 * Arrays go over as comma-joined strings; unset keys are omitted entirely so an untouched
 * control never reaches the builder as a constraint.
 */
export function toParams(values, params = new URLSearchParams()) {
  for (const key of ALL_KEYS) {
    const v = values[key];
    if (!isSet(v)) continue;
    params.append(key, Array.isArray(v) ? v.join(",") : v);
  }
  return params;
}

/** How many filters (not value keys) are currently set — for the "N active" badge. */
export function activeCount(values) {
  return FILTERS.filter((f) => keysFor(f).some((k) => isSet(values[k]))).length;
}

/**
 * Normalise a stored/serialised payload back into control-shaped values.
 * Saved searches hold numbers where the controls hold strings, and arrays may be absent.
 */
export function fromStored(stored = {}) {
  const out = emptyValues();
  for (const f of FILTERS) {
    for (const k of keysFor(f)) {
      const v = stored[k];
      if (v === null || v === undefined) continue;
      out[k] = f.control === "chips" ? (Array.isArray(v) ? v : [v]) : String(v);
    }
  }
  return out;
}

/** Values -> the JSON a saved search stores (numbers as numbers, unset keys dropped). */
export function toStored(values) {
  const out = {};
  for (const f of FILTERS) {
    for (const k of keysFor(f)) {
      const v = values[k];
      if (!isSet(v)) continue;
      if (Array.isArray(v)) out[k] = v;
      else if (f.control === "daterange" || Number.isNaN(Number(v))) out[k] = v;
      else out[k] = Number(v);
    }
  }
  return out;
}
